import { ScheduledWorkflow } from "@/reducers/openclawWorkflowsSlice"
import { useState } from "react"
import { Sparkle } from "lucide-react"
import { useTranslation } from "react-i18next"
import FleetReminderSection from "./FleetReminderSection"
import { StopAutomationEditDialog } from "@/components/routeStops/StopAutomationEditDialog"
import { CronAutomationDialog } from "./CronAutomationDialog"
import { AutomationDetailsDialog } from "./AutomationDetailsDialog"
import { useIsDriverWorkspace } from "@/contexts/DriverWorkspaceContext"
import { useVisibleWorkflows } from "@/hooks/useVisibleWorkflows"
import {
  isEditable,
  isScheduleRule,
  isStopRule,
  ScheduledGroup,
  sortActiveFirst,
  TRANSPORT_BRIEF_DIGEST_URL,
  TransportBriefRow,
  WorkflowItem,
} from "./workflowRows"

/** The rules I own, the only ones this panel may write. */
const PersonalSection = ({
  jobs,
  onEdit,
  onView,
}: {
  jobs: ScheduledWorkflow[]
  /** Omitted when the driver workspace is off — rows stay read-only then. */
  onEdit?: (job: ScheduledWorkflow) => void
  onView?: (job: ScheduledWorkflow) => void
}) => {
  const { t } = useTranslation()
  if (jobs.length === 0) return null

  return (
    <section>
      <p className="mb-1 text-xs font-medium text-muted-foreground">
        {t("ai.automations.sections.personal")}
      </p>
      <div className="flex flex-col">
        {(sortActiveFirst(jobs) as ScheduledWorkflow[]).map((job) => (
          <WorkflowItem
            key={job.id}
            job={job}
            // Every personal rule is yours to manage — editable ones open
            // their form, everything else (geofence, fuel-norm) opens a
            // read-only view with just Delete/Pause.
            onEdit={isEditable(job) ? onEdit : onView}
          />
        ))}
      </div>
    </section>
  )
}

export { TRANSPORT_BRIEF_DIGEST_URL }

export const BriefingSection = () => {
  const { t } = useTranslation()
  return (
    <section>
      <p className="mb-1 text-xs font-medium text-muted-foreground">
        {t("ai.automations.sections.briefing")}
      </p>
      <TransportBriefRow />
    </section>
  )
}

// Shown as examples only; the user types their own request.
const EMPTY_EXAMPLES = [
  "ai.automations.empty.examples.dailySummary",
  "ai.automations.empty.examples.driverSilent",
  "ai.automations.empty.examples.weeklyReport",
] as const

const WorkflowList = ({ showHeader = true }: { showHeader?: boolean }) => {
  const { t } = useTranslation()
  const {
    personal: myLocalJobs,
    shared: globalJobs,
    fleetReminders,
    count,
  } = useVisibleWorkflows()
  const [editing, setEditing] = useState<ScheduledWorkflow | null>(null)
  const [viewing, setViewing] = useState<ScheduledWorkflow | null>(null)

  // This list renders both in the driver workspace's widget panel and in the
  // global Slick AI sidebar. Outside the workspace it must look exactly as it
  // always has, so the layout context — not the flag — decides.
  const inWorkspace = useIsDriverWorkspace()

  const personalSection = (
    <PersonalSection
      jobs={myLocalJobs}
      // Editing an automation from the list is part of the driver workspace;
      // without it rows stay read-only, as before.
      onEdit={inWorkspace ? setEditing : undefined}
      onView={inWorkspace ? setViewing : undefined}
    />
  )

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {showHeader && (
        <div className="flex min-h-16 items-center border-b border-gray-100 px-4 dark:border-gray-800">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t("ai.automations.title")}
          </h2>
        </div>
      )}

      {count === 0 ? (
        <div className="flex flex-col gap-4 px-6 pt-6">
          <BriefingSection />
          <div className="flex flex-col items-center gap-2 text-center">
            <Sparkle
              className="h-9 w-9 text-violet-300 dark:text-violet-600"
              strokeWidth={1.5}
            />
            <p className="text-base font-medium text-gray-700 dark:text-gray-300">
              {t("ai.automations.empty.title")}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {t("ai.automations.empty.hint")}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {EMPTY_EXAMPLES.map((example) => (
              <div
                key={example}
                className="rounded-lg border border-gray-100 px-3 py-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400"
              >
                &quot;{t(example)}&quot;
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          <BriefingSection />
          {/* Personal sits last in the workspace, where it carries the "create"
              actions and wants to be next to them. Outside it, it keeps its
              original place directly under the brief. */}
          {!inWorkspace && personalSection}
          {/* Shared rules belong to someone else — the agent rejects a write
              from a non-owner, so they stay read-only here. */}
          <ScheduledGroup
            label={t("ai.automations.sections.shared")}
            jobs={globalJobs}
          />
          {fleetReminders.length > 0 && (
            <section>
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {t("ai.automations.sections.fleetReminders")}
              </p>
              <div className="flex flex-col gap-3">
                {fleetReminders.map((reminder) => (
                  <FleetReminderSection key={reminder.id} reminder={reminder} />
                ))}
              </div>
            </section>
          )}
          {inWorkspace && personalSection}
        </div>
      )}

      <StopAutomationEditDialog
        job={editing && isStopRule(editing) ? editing : null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <CronAutomationDialog
        job={editing && isScheduleRule(editing) ? editing : null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <AutomationDetailsDialog
        job={viewing}
        onOpenChange={(open) => !open && setViewing(null)}
      />
    </div>
  )
}

export default WorkflowList
